import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  AlloyButton,
  ModalContext,
  PageLoader,
  useModal
} from 'cat-ecommerce-alloy';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import Espot from '@app/components/common/Espot/Espot';
import StopEditingQuote from '@app/components/pages/summary-and-payment/StopEditingQuote';
import ConflictNotes from '@app/components/saved-lists/ConflictNotes';
import ExceptionFeedbackBySelector from '../../../../common/Exception/ExceptionFeedbackBySelector';
import OrderInformation from '../OrderInformation';
import OrderItems from '../OrderItems';
import RefreshContactButtons from '../OrderItems/common/RefreshContactButtons/RefreshContactButtons';
import OrderSummary from '../OrderSummary/OrderSummary';
import QuickOrder from '../QuickOrder';
import CartDisclaimers from './CartDisclaimers';
import CartImportSummaryModal from './CartImportSummaryModal';
import GuestSignIn from './GuestSignIn';
import ShoppingCartDropZone from './ShoppingCartDropZone';
import ShoppingCartOutageWarning from './ShoppingCartOutageWarning';
import AddToCartModal from './AddToCartModal';
import WarrantyAndPolicyLinks from './WarrantyAndPolicyLinks';
import ShoppingCartPageSubHeader from './ShoppingCartPageSubHeader';
import TopEspots from './TopEspots';
import SingleByteExceptionMessage from '../../SingleByteExceptionMessage';
import useGACartEvents from './hooks/useGACartEvents';
import useEspot from '@app/hooks/useEspot';
import useSisCartCookie from '../../hooks/useSisCartCookie';
import useLogin from '@app/components/login/hooks/useLogin';
import { useBreakpoint, useQueryParams } from '@app/hooks';
import { displaySaveToListModal } from '@app/services/legacyJSService';
import {
  getCookie,
  getSessionStorage,
  removeCookie,
  replaceTokensInString,
  setSessionStorage
} from '@app/utils';
import { createHiddenItemsFields } from '@app/components/pages/checkout/shopping-cart/OrderItems/utils/OrderItemsUtils';
import {
  updateSessionStorageCartSharingData,
  filteredValue,
  getItemsFromCILGCookie,
  setTimeSessionStorage,
  getSessionStorageCartSharingData,
  redirectToHomePage
} from './utils';
import {
  CART_REQUEST_TYPE,
  CSR_SHARE_CART_LINK_FLOW_PARAM,
  CSR_SHARE_CART_LINK_FLOW_PARAM_REGISTERED_USERS_VALUE,
  CSR_SHARE_CART_LINK_STOREID_PARAM,
  CSR_SHARE_CART_LINK_STORE_LOCATION_ID_PARAM
} from '../OrderItems/constants';
import {
  ADD_TO_CART_PHASES,
  CART_ESPOT_BOTTOM,
  CART_ESPOT_IE,
  CART_ESPOT_PINSTRIPE,
  CART_ESPOT_TOP,
  CHECKOUT_INITIAL_TIME,
  CILG,
  DEFAULT_ROW_VALUES,
  DEFAULT_SHOPPING_CART_MAX_ITEMS,
  FORMIK_INITAL_VALUES,
  MATCH_ROUTE_DEEPLINK
} from './constants';
import {
  ADD_TO_CART_SET_PHASE,
  CHECKOUT_CART_FORM_SUBMIT_BEGIN,
  CHECKOUT_CART_FORM_SUBMIT_FAIL,
  CHECKOUT_CART_FORM_SUBMIT_SUCCESS,
  IMPORT_MODAL_VISIBLE_CHECKOUT,
  SET_IS_ADD_TO_CART_AND_CHECKOUT_FLOW
} from '@app/store/checkout/constants';
import { SIS_PARTS_LIST } from '@app/components/login/redirect/constants';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import { SIS_CART_PARTS_LIST } from '@app/constants/cookieContants';
import {
  AFFILIATION_DEALER,
  AVAIL_BY_DATE,
  AVAIL_BY_SOURCE,
  ESPOT_VARIANTS,
  JAPANESE_LANGUAGE_CODE,
  PAGE_BREAKPOINTS,
  STATUS,
  USER_TYPE_GUEST
} from '@app/constants/commonConstants';
import {
  SOURCE_OF_SUPPLY_API_INITIAL_TIME,
  SOURCE_OF_SUPPLY_API
} from '@app/constants/analyticsConstants';
import useCalculateGATiming from '@app/components/pages/checkout/hooks/useCalculateGATiming/useCalculateGATiming';
import {
  setQuickOrderFlow,
  updateDateAndEquipment
} from '@app/store/checkout/actions';
import { clearError } from '@app/store/exception/actions';
import { loadPreferences } from '@app/store/shoppingPreferences/actions';
import styles from './styles.module.scss';
import ReplacementParts from './ReplacementParts/ReplacementParts';
import { useMyAccountLinks } from '@app/components/pages/account/hooks';
import { CUSTOM_OUTAGE_MESSAGE } from '@app/components/global-banners/constants';

const ShoppingCartPage = ({ location }) => {
  const [t] = useTranslation();
  const { addModal } = useContext(ModalContext);
  const { push } = useHistory();
  const dispatch = useDispatch();
  const { parse, stringify } = queryString;
  const { login } = useLogin();
  const { pathname, search } = location;
  const { getItemsSisCartCookie } = useSisCartCookie();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const qoMobileModalProps = useModal();
  const queryParams = useQueryParams();
  const { staticLinks } = useMyAccountLinks();
  const baseHomeUrl = staticLinks.HOME.url;

  useCalculateGATiming(SOURCE_OF_SUPPLY_API_INITIAL_TIME, SOURCE_OF_SUPPLY_API);

  const { isShoppingCartImportModalDisplay, systemFeedBack } = useSelector(
    state => state.checkout.deepLink
  );
  const userType = useSelector(state => state.common.userType);
  const areTranslationsLoaded = useSelector(
    s => s.common.areTranslationsLoaded
  );
  const requestByDateEnabled = useSelector(
    s => s.orders.requestByDate?.requestByDateEnabled
  );
  const isLoggedIn = !!(userType && userType !== USER_TYPE_GUEST);
  const isGuestUser = userType && userType === USER_TYPE_GUEST;
  const isLg = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const current = useSelector(s => s.orders.current);
  const shoppingPreferenceDataStatus = useSelector(
    s => s.shoppingPreferences.shoppingPreferenceDataStatus
  );
  const sisCookie = getCookie(SIS_PARTS_LIST) || '';

  const shoppingPreferencesResolved =
    shoppingPreferenceDataStatus === STATUS.RESOLVED;

  const shoppingPreferencesLoading =
    shoppingPreferenceDataStatus === STATUS.PENDING ||
    shoppingPreferenceDataStatus === STATUS.IDLE;
  const getCurrentOrderLoading = useSelector(
    s => s.orders.getCurrentOrderLoading
  );
  const shoppingCartMaxItems =
    useSelector(s => s.dealer.shoppingCartMaxItems) ??
    DEFAULT_SHOPPING_CART_MAX_ITEMS;
  const currentOrder = useSelector(s => s.orders.byId?.[current]);
  const numItemsInCart = currentOrder?.ascendingOrderItems?.length ?? 0;
  const showGuestSignIn = !isLoggedIn && !getCurrentOrderLoading;
  const { url, urlDisplayName, title } =
    systemFeedBack?.length > 0 && systemFeedBack[0];
  const shoppingPreferencesEnabled = useSelector(
    s => s.dealer.shoppingPreferencesEnabled
  );
  const cartFormSubmitStatus = useSelector(
    s => s.checkout.cartFormSubmitStatus
  );
  const addToCartSubmitStatus = useSelector(
    s => s.checkout.addToCartSubmitStatus
  );
  const ascendingOrderItems = useSelector(
    s => s.orders.byId?.[current]?.ascendingOrderItems || []
  );
  const isInvalidParts = useSelector(state => state.checkout?.isInvalidParts);
  const addToCartBtnLoading = addToCartSubmitStatus === STATUS.PENDING;
  const customerType = useSelector(
    s => s.shoppingPreferences.shoppingPreferencesData.customerType
  );
  const isNotIAUser = customerType && customerType !== 'IA';
  const isInstantAccess = useSelector(s => s.common.isInstantAccess);
  const formSubmitProceedToCheckoutLoading =
    cartFormSubmitStatus === STATUS.PENDING;
  const addToCartStatus = useSelector(s => s.checkout.addToCartStatus);
  const addToCartLoading = addToCartStatus === STATUS.PENDING;
  const sosItemDetailsStatus = useSelector(
    s => s.checkout.sosItemDetailsStatus
  );
  const currentAddToCartHasSOS = useSelector(
    s => s.checkout.currentAddToCartHasSOS
  );
  const dealerPriceAndAvailabilityStatus = useSelector(
    s => s.checkout.dealerPriceAndAvailabilityStatus
  );
  const isDealerPriceAndAvailabilityLoading =
    dealerPriceAndAvailabilityStatus === STATUS.PENDING;
  const sosItemDetailsLoading = sosItemDetailsStatus === STATUS.PENDING;
  const showPageLoader =
    formSubmitProceedToCheckoutLoading ||
    sosItemDetailsLoading ||
    addToCartLoading ||
    (addToCartBtnLoading && isMD) ||
    (!currentAddToCartHasSOS && isDealerPriceAndAvailabilityLoading);
  let exceptionFeedbackSelector = s => s.errors;
  let exceptionFeedbackError = false;
  const xorderError = useSelector(
    s => s.errors?.[ERROR_DOMAIN.CART]?.[ERROR_PATH.MAIN]
  );
  if (xorderError) {
    exceptionFeedbackSelector = s =>
      s.errors?.[ERROR_DOMAIN.CART]?.[ERROR_PATH.MAIN];
  }
  const shoppingPreferenceError = useSelector(
    s => s.errors?.[ERROR_DOMAIN.PREFERENCES]?.[ERROR_PATH.MAIN]
  );
  if (isLoggedIn && shoppingPreferenceError) {
    exceptionFeedbackSelector = s =>
      s.errors?.[ERROR_DOMAIN.PREFERENCES]?.[ERROR_PATH.MAIN];
  }
  const requiredFieldsError = useSelector(
    s => s.errors?.[ERROR_DOMAIN.CHECKOUT]?.[ERROR_PATH.MAIN]
  );

  if (requiredFieldsError) {
    exceptionFeedbackSelector = s =>
      s.errors?.[ERROR_DOMAIN.CHECKOUT]?.[ERROR_PATH.MAIN];
  }
  exceptionFeedbackError = !!(
    xorderError ||
    shoppingPreferenceError ||
    requiredFieldsError
  );
  if (
    xorderError?.severity === 'success' ||
    title === t('IMPORT_UNSUCCESSFUL')
  ) {
    exceptionFeedbackError = false;
  }
  const originalQuoteId = useSelector(
    state =>
      state.orders?.byId?.[state.orders.current]?.orderExtendAttribute
        ?.originalQuoteId
  );
  const originalQuoteNumber = useSelector(
    state =>
      state.orders?.byId?.[state.orders.current]?.orderExtendAttribute
        ?.originalQuoteNumber
  );

  const currentOrderItems = currentOrder?.ascendingOrderItems;
  const dealerName = useSelector(s => s.dealer.dealerName);
  const registrationPendingApproval = useSelector(
    s => s.common?.registrationPendingApproval
  );
  const shoppingPreferencesError = useSelector(
    s => s.shoppingPreferences.error
  );
  const showAvailabilityBySource = useSelector(
    state =>
      !!state?.shoppingPreferences?.shoppingPreferencesData
        ?.showSourcingDetailToggle
  );
  const userAffiliation = useSelector(s => s.common.userAffiliation);
  const isDealerUser = userAffiliation === AFFILIATION_DEALER;
  const key = showAvailabilityBySource ? AVAIL_BY_DATE : AVAIL_BY_SOURCE;
  const loadSisModal = isInvalidParts || !isMD;

  const pendingApprovalUser =
    registrationPendingApproval ||
    (customerType === undefined &&
      isInstantAccess &&
      !isGuestUser &&
      !isDealerUser &&
      !registrationPendingApproval);
  const [isToggleDetails, setIsToggleDetails] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleToggleDetailsChange = newValue => {
    setIsToggleDetails(newValue);
    setSessionStorage(key, newValue);
  };

  const { fireGAEventFunction } = useGACartEvents();

  useEffect(() => {
    const newValue = getSessionStorage(key) === 'true';
    setIsToggleDetails(newValue);
  }, [key, t]);

  useEffect(() => {
    const cartLinkStoreId = queryParams.get(CSR_SHARE_CART_LINK_STOREID_PARAM);
    if (
      queryParams.get(CSR_SHARE_CART_LINK_FLOW_PARAM) ===
        CSR_SHARE_CART_LINK_FLOW_PARAM_REGISTERED_USERS_VALUE &&
      cartLinkStoreId &&
      queryParams.get(CSR_SHARE_CART_LINK_STORE_LOCATION_ID_PARAM)
    ) {
      const { previousStoreId = null } =
        getSessionStorageCartSharingData() || {};

      updateSessionStorageCartSharingData({
        storeId: cartLinkStoreId,
        storeLocationId: queryParams.get(
          CSR_SHARE_CART_LINK_STORE_LOCATION_ID_PARAM
        )
      });

      if (isLoggedIn && previousStoreId !== cartLinkStoreId) {
        updateSessionStorageCartSharingData({
          cartLinkUrl: window.location.href
        });
        redirectToHomePage(previousStoreId, baseHomeUrl);
      }
    }
  }, [isLoggedIn, queryParams, baseHomeUrl]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(loadPreferences({ requestType: CART_REQUEST_TYPE }));
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      const isGuestSavedListHit =
        getCookie('guestSavedListHit') === 'true' ? true : false;
      if (isGuestSavedListHit && ascendingOrderItems.length > 0) {
        displaySaveToListModal();
        createHiddenItemsFields(ascendingOrderItems);
        removeCookie('guestSavedListHit');
      }
    }
  }, [ascendingOrderItems]);

  useEffect(() => {
    const { route } = parse(search);
    if (route && route === MATCH_ROUTE_DEEPLINK) {
      const cookieValue = getItemsFromCILGCookie();
      const addtoCartItems = filteredValue(cookieValue, ascendingOrderItems);
      fireGAEventFunction(addtoCartItems, false, false, CILG);
    }
  }, [ascendingOrderItems, fireGAEventFunction, parse, search]);

  useEffect(() => {
    const removeDeeplinkRoute = (path, params) =>
      push(`${path}?${stringify(params)}`);
    const { route, ...restParams } = parse(search);

    if (route && route === MATCH_ROUTE_DEEPLINK) {
      if (isShoppingCartImportModalDisplay && areTranslationsLoaded) {
        addModal(<CartImportSummaryModal />);
        removeDeeplinkRoute(pathname, restParams);
      } else if (isShoppingCartImportModalDisplay === false) {
        removeDeeplinkRoute(pathname, restParams);
      }
    }
  }, [
    addModal,
    location,
    parse,
    pathname,
    push,
    search,
    stringify,
    isShoppingCartImportModalDisplay,
    areTranslationsLoaded
  ]);

  const errorInfo = {
    domain: ERROR_DOMAIN.QUICK_ORDER,
    message: replaceTokensInString(
      t('QUICK_ORDER_MAX_LINES'),
      shoppingCartMaxItems
    ),
    path: ERROR_PATH.MAIN
  };
  /**
   * to avoid multiple render that causes redux refrenceId is not matching with formik refrenceID added firstRender
   */
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current && getCookie(SIS_CART_PARTS_LIST)) {
      getItemsSisCartCookie();
      firstRender.current = false;
    }
  }, [getItemsSisCartCookie]);

  /**
   * When there is a pending approval, error is shown on cart because of missing userPreferences
   * To avoid churn with modifying the action (loadPreferences in shoppingPreferences/actions.js)
   * we clear the error here
   */
  useEffect(() => {
    if (shoppingPreferencesError && pendingApprovalUser) {
      dispatch(clearError(ERROR_DOMAIN.PREFERENCES, ERROR_PATH.MAIN));
    }
  }, [dispatch, pendingApprovalUser, shoppingPreferencesError]);

  const initialValues = FORMIK_INITAL_VALUES;
  const onSubmit = useCallback(
    async (
      values,
      { setTouched, validateForm, submitForm },
      shouldCheckout
    ) => {
      dispatch(setQuickOrderFlow(true));
      if (shouldCheckout) {
        dispatch({ type: CHECKOUT_CART_FORM_SUBMIT_BEGIN });
      }
      const touchedItems = values.items.map(item => ({
        ...item,
        itemNumber: true,
        quantity: true
      }));
      await setTouched({
        ...(shouldCheckout
          ? {
              equipment: true,
              requestByDate: true
            }
          : {}),
        items: touchedItems
      });

      const formErrors = shouldCheckout ? await validateForm() : {};
      if (isEmpty(formErrors)) {
        if (shouldCheckout) {
          await dispatch(
            updateDateAndEquipment({
              ...values,
              calledFrom: IMPORT_MODAL_VISIBLE_CHECKOUT
            })
          );
        }

        if (shouldCheckout) {
          dispatch({
            type: SET_IS_ADD_TO_CART_AND_CHECKOUT_FLOW,
            payload: true
          });
        }
        dispatch({
          type: ADD_TO_CART_SET_PHASE,
          payload: ADD_TO_CART_PHASES.CALLING_SOS_SERVICE
        });

        if (shouldCheckout) {
          fireGAEventFunction(currentOrderItems, true);
          const EQUIPMENT_VALUE = 'equipmentValue';
          const REQUEST_BY_DATE = 'requestByDate';
          sessionStorage.removeItem(EQUIPMENT_VALUE);
          sessionStorage.removeItem(REQUEST_BY_DATE);
          setTimeSessionStorage(CHECKOUT_INITIAL_TIME);
          dispatch({ type: CHECKOUT_CART_FORM_SUBMIT_SUCCESS });
        }
      } else {
        submitForm();
        dispatch({ type: CHECKOUT_CART_FORM_SUBMIT_FAIL });
      }
    },
    [currentOrderItems, dispatch, fireGAEventFunction, t]
  );

  const espotNames = [
    CART_ESPOT_PINSTRIPE,
    CART_ESPOT_TOP,
    CART_ESPOT_IE,
    CART_ESPOT_BOTTOM,
    CUSTOM_OUTAGE_MESSAGE
  ].join(',');
  const { getEspotContent } = useEspot(espotNames);
  const espotHtml = getEspotContent(CART_ESPOT_BOTTOM);
  const topEspotData = {
    [CART_ESPOT_PINSTRIPE]: getEspotContent(CART_ESPOT_PINSTRIPE),
    [CART_ESPOT_TOP]: getEspotContent(CART_ESPOT_TOP),
    [CART_ESPOT_IE]: getEspotContent(CART_ESPOT_IE)
  };
  const bottomEspot =
    (isLoggedIn && shoppingPreferencesEnabled && espotHtml && !isDealerUser && (
      <Espot
        html={espotHtml}
        className="d-print-none"
        variant={ESPOT_VARIANTS.LARGE}
      />
    )) ||
    null;
  const itemsToTouch = Array.from(Array(shoppingCartMaxItems).keys()).map(
    () => ({
      itemNumber: true
    })
  );

  const showQuickOrder = isLoggedIn && !pendingApprovalUser;
  const showOrderInfo =
    (isLoggedIn || requestByDateEnabled) && !pendingApprovalUser;
  const feedBackSize = title === t('IMPORT_UNSUCCESSFUL') ? 'medium' : 'large';

  const hasTranslation = () => {
    if (title && t(title)) {
      return false;
    } else if (title && !t(title)) {
      return true;
    }
  };
  return (
    <Formik
      initialValues={initialValues}
      initialTouched={{ items: itemsToTouch }}
      validateOnChange={false}
      validateOnBlur
      validateOnMount
    >
      {({ values }) => {
        return (
          <div className="container px-md-0 pt-md-3">
            <ShoppingCartDropZone
              defaultRowValues={DEFAULT_ROW_VALUES}
              errorInfo={errorInfo}
              shoppingCartMaxItems={shoppingCartMaxItems}
            >
              <TopEspots espotData={topEspotData} />
              <ShoppingCartOutageWarning />
              <div className={cx({ 'mt-3': !!originalQuoteNumber })}>
                {originalQuoteNumber && (
                  <StopEditingQuote
                    originalQuoteNumber={originalQuoteNumber}
                    originalQuoteId={originalQuoteId}
                    orderId={current}
                  />
                )}
              </div>
              <ConflictNotes />
              <h1 className={cx('h1 display-1 w-100 mb-3 d-print-none')}>
                {t('SHOPPINGCART_TITLE')}
              </h1>
              <span
                className={cx(
                  `${styles['shopping-cart-page__heading--print']} text-univers--bold text-uppercase mb-2 d-none d-print-block`
                )}
              >
                {`${dealerName} ${t('SHOPPINGCART_TITLE')}`}
              </span>
              <SingleByteExceptionMessage languageId={JAPANESE_LANGUAGE_CODE} />
              <ExceptionFeedbackBySelector
                className="mb-2"
                selector={exceptionFeedbackSelector}
                messageClassName={exceptionFeedbackError ? 'u-fs--12' : ''}
                skipTranslation={hasTranslation()}
              >
                {url && (
                  <div className="justify-content-start">
                    <AlloyButton
                      buttonType="ghost"
                      className="p-0 text-uppercase"
                      size={feedBackSize}
                      onClick={() => (window.location.href = url)}
                    >
                      {urlDisplayName}
                    </AlloyButton>
                  </div>
                )}
                {isGuestUser && xorderError?.totalItemsAdded > 0 && (
                  <div className="pt-2">
                    {t('HAVE_AN_ACCOUNT')}
                    <AlloyButton
                      buttonType="link"
                      size="large"
                      onClick={login}
                      className="px-2 py-1 u-fs--14"
                    >
                      {t('SIGN_IN')}
                    </AlloyButton>
                  </div>
                )}

                {exceptionFeedbackError && <RefreshContactButtons />}
              </ExceptionFeedbackBySelector>

              {isNotIAUser && (
                <ExceptionFeedbackBySelector
                  className="mb-2"
                  messageClassName="u-color--DarkGray"
                  selector={s =>
                    s.errors[ERROR_DOMAIN.DEALER_CUSTOMER_DCN]?.[
                      ERROR_PATH.MAIN
                    ]
                  }
                ></ExceptionFeedbackBySelector>
              )}
              <Form className="w-100 d-flex flex-column">
                {showPageLoader && <PageLoader />}
                <ShoppingCartPageSubHeader numItemsInCart={numItemsInCart} />
                <div
                  className={`row ${styles['shopping-cart-page__container']}`}
                >
                  <div
                    className={`d-print-flex col col-lg-9 ${
                      styles[('col-print-12', 'col-order-2')]
                    } pt-1`}
                  >
                    {showQuickOrder && (
                      <QuickOrder
                        errorInfo={errorInfo}
                        initialItems={initialValues.items}
                        shoppingCartMaxItems={shoppingCartMaxItems}
                        isAvailabilityToggled={isToggleDetails}
                        setIsAvailabilityToggled={handleToggleDetailsChange}
                        onAddToCartClick={onSubmit}
                        openQOMobileModal={qoMobileModalProps.open}
                      />
                    )}
                    {sisCookie
                      ? loadSisModal && (
                          <AddToCartModal
                            parentModalProps={qoMobileModalProps}
                            isModalTypeSis
                            isAvailabilityToggled={isToggleDetails}
                            setIsAvailabilityToggled={handleToggleDetailsChange}
                            isMd={isMD}
                            shoppingPreferencesResolved={
                              shoppingPreferencesResolved
                            }
                            onAddToCartClick={onSubmit}
                          />
                        )
                      : !isMD &&
                        showQuickOrder && (
                          <AddToCartModal
                            modalOpen={isModalOpen}
                            setModalOpen={setIsModalOpen}
                            parentModalProps={qoMobileModalProps}
                            errorInfo={errorInfo}
                            initialItems={initialValues.items}
                            isMd={isMD}
                            shoppingCartMaxItems={shoppingCartMaxItems}
                            isAvailabilityToggled={isToggleDetails}
                            setIsAvailabilityToggled={handleToggleDetailsChange}
                            onAddToCartClick={onSubmit}
                          />
                        )}
                    <OrderItems
                      modalOpen={setIsModalOpen}
                      displayAvailabityAndSourcingDetails={isToggleDetails}
                      setTogglePrefs={handleToggleDetailsChange}
                      showNotes={values.showNotes}
                    />
                  </div>
                  <div
                    className={`d-print-flex ${
                      styles[('col-print-12', 'col-order-1')]
                    } col-12 col-lg-3 `}
                  >
                    {showOrderInfo && <OrderInformation />}
                    {showGuestSignIn && !isLg && (
                      <GuestSignIn numItemsInCart={numItemsInCart} />
                    )}
                    <div className={styles['col-print-6']}>
                      <OrderSummary />
                    </div>
                  </div>
                </div>
              </Form>
              {showGuestSignIn && isLg && (
                <GuestSignIn numItemsInCart={numItemsInCart} />
              )}
              {!(shoppingPreferencesLoading && isLoggedIn) && (
                <>
                  {numItemsInCart > 0 && <WarrantyAndPolicyLinks />}
                  <CartDisclaimers />
                </>
              )}
              {bottomEspot}
            </ShoppingCartDropZone>
            <ReplacementParts
              isAvailabilityToggled={isToggleDetails}
              setIsAvailabilityToggled={handleToggleDetailsChange}
            />
          </div>
        );
      }}
    </Formik>
  );
};

ShoppingCartPage.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string
  })
};

export default ShoppingCartPage;
